import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  equipmentApi,
  type ApiEventStats,
  type ApiMaintenanceEvent,
  type CreateEventPayload,
  type EndEventPayload,
  type EventStatsQuery,
  type ListEventsQuery,
  type StartEventPayload,
  type UndoEndPayload,
  type UpdateEventPayload,
} from '@/api/equipmentApi'
import { mockMaintenanceTasks } from '@/mocks/maintenanceTasks'
import { deriveStatus, todayIso, withStatusOverride } from '@/lib/maintenanceStatus'
import { userDisplayName } from '@/lib/userDisplayName'
import { useMockApi } from '@/lib/useMock'
import { useTenant } from '@/providers/TenantProvider'
import { PENDING_ACTIONS_KEY } from '@/hooks/usePendingActions'
import { FACILITY_OVERVIEW_KEY } from '@/hooks/useFacility'
import { UNASSIGNED_LABEL, type MaintenanceTask, type TaskStatus } from '@/types/maintenanceTask'

export type MyListStats = ApiEventStats

/** The stat cards' rules, over a list of rows: the mock worklist's counts. */
function statsOf(tasks: MaintenanceTask[]): MyListStats {
  const today = todayIso()
  const due = tasks.filter((t) => t.status === 'due' && !t.overdue)
  const dueToday = due.filter((t) => t.dueDate === today).length
  return {
    total: tasks.length,
    dueToday,
    // On time: overdue is its own card, started or not, so nothing counts twice.
    inProgress: tasks.filter((t) => t.status === 'started' && !t.overdue).length,
    overdue: tasks.filter((t) => t.overdue).length,
    completed: tasks.filter((t) => t.status === 'completed').length,
    notStarted: due.length - dueToday,
    dueTodayAll: dueToday,
  }
}

const NO_STATS: MyListStats = { total: 0, dueToday: 0, inProgress: 0, overdue: 0, completed: 0, notStarted: 0, dueTodayAll: 0 }

/**
 * The Tasks page's stat cards, counted by the server (z8nrz7f9u6) over the same
 * tasks the list shows — its range, subjects and Resource filter — so they add
 * up to its total (z8nrz7fjzc), without downloading the rows twice. Keyed under
 * 'maintenance-events', so every Start / End / save refreshes it.
 */
export function useTaskStats(params: EventStatsQuery): MyListStats {
  const { selectedTenantId } = useTenant()
  const query = useQuery({
    queryKey: ['maintenance-events', selectedTenantId, 'stats', params] as const,
    enabled: selectedTenantId !== null && !useMockApi,
    queryFn: () => equipmentApi.eventStats(selectedTenantId as string, params),
  })
  if (useMockApi) return statsOf(mockMaintenanceTasks)
  return query.data ?? NO_STATS
}

/** Map a persisted MaintenanceEvent onto the worklist row shape. */
export function apiEventToTask(ev: ApiMaintenanceEvent): MaintenanceTask {
  return {
    id: ev.id,
    taskClass: ev.taskClass === 'corrective' ? 'corrective' : ev.taskClass === 'inventory' ? 'inventory' : 'preventive',
    // A task on a material (z8nrz7fjzc) reads as the material, and its lot when
    // it is on one: name, then type. One on a purchase order reads as the order.
    equipmentId:
      ev.equipment?.label ??
      (ev.material
        ? `${ev.material.materialName}${ev.lot ? ` · Lot ${ev.lot.lotNumber}` : ''}`
        : (ev.purchaseOrder?.number ?? ev.equipmentId ?? '')),
    usedBy: ev.usedBy ?? ev.equipmentType?.name ?? ev.material?.materialType ?? ev.purchaseOrder?.supplierName ?? '',
    material:
      ev.material && ev.materialId
        ? {
            id: ev.materialId,
            code: ev.material.materialCode,
            type: ev.material.materialType,
            lot: ev.lot && ev.lotId ? { id: ev.lotId, number: ev.lot.lotNumber } : undefined,
          }
        : undefined,
    purchaseOrder:
      ev.purchaseOrder && ev.purchaseOrderId
        ? { id: ev.purchaseOrderId, number: ev.purchaseOrder.number, supplierName: ev.purchaseOrder.supplierName }
        : undefined,
    fromAction: ev.source === 'TRIGGER' || undefined,
    eventType: ev.eventTypeCode,
    priority: ev.priority,
    earlyStart: ev.earlyStart ? ev.earlyStart.slice(0, 10) : undefined,
    dueDate: ev.dueDate.slice(0, 10),
    createdAt: ev.createdAt,
    planHours: ev.planHours ?? 0,
    // Real assignee name, from the feed's assignee include. `username` is part
    // of that order and used to be skipped, so an account with no full name
    // showed a raw email address in a column headed Assigned — see
    // `userDisplayName`, which mirrors the backend's own fallback chain.
    assigned: userDisplayName(ev.assignee, UNASSIGNED_LABEL),
    assigneeId: ev.assigneeId ?? undefined,
    sopRef: ev.sopRef ?? undefined,
    repeatCycle: ev.repeatCycle ?? undefined,
    status: deriveStatus(ev),
    overdue: ev.overdue ?? false,
    daysOverdue: ev.daysOverdue ?? null,
    completedLate: ev.completedLate ?? false,
    unitOutOfService: ev.equipment?.status === 'OUT_OF_SERVICE',
    checklist: ev.preStartChecklist,
    procedureTemplate: ev.procedureTemplate ?? undefined,
  }
}

/**
 * The current technician's maintenance worklist, backed by GET /equipment/events
 * (persisted MaintenanceEvent model, subtask 86ey7d8n2). Start/End are still
 * local, optimistic status moves until the e-signed execution flow lands
 * (86ey7d8p8); New Event stays local until 86ey7d8pq. In VITE_USE_MOCK mode it
 * falls back to the demo fixtures so the tab stays populated offline.
 */
export function useMyList(query: ListEventsQuery = {}) {
  const { selectedTenantId } = useTenant()
  const tenantId = useMockApi ? 'mock' : selectedTenantId
  const qc = useQueryClient()

  const eventsQuery = useQuery({
    queryKey: ['maintenance-events', tenantId, query] as const,
    enabled: tenantId !== null && !useMockApi,
    queryFn: () => equipmentApi.listEvents(tenantId as string, query),
  })

  // Optimistic status overrides applied on Start/End/Undo so the row moves
  // immediately; the real status arrives when the events query refetches. New
  // Event rows (86ey7d8pq) stay local until that endpoint exists.
  const [overrides, setOverrides] = useState<Record<string, TaskStatus>>({})
  const [localAdded, setLocalAdded] = useState<MaintenanceTask[]>([])

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['maintenance-events'] })
    // A task's Start, End, Undo or edit changes its room's status on the
    // Overall View (Cleaning Visual): refresh it now, not at its next poll.
    void qc.invalidateQueries({ queryKey: [FACILITY_OVERVIEW_KEY] })
  }, [qc])
  const revertOverride = useCallback((id: string) => {
    setOverrides((o) => {
      const next = { ...o }
      delete next[id]
      return next
    })
  }, [])

  const startMut = useMutation({
    mutationFn: (v: { id: string; payload: StartEventPayload }) =>
      equipmentApi.startEvent(tenantId as string, v.id, v.payload),
    onSuccess: invalidate,
  })
  const endMut = useMutation({
    mutationFn: (v: { id: string; payload: EndEventPayload }) =>
      equipmentApi.endEvent(tenantId as string, v.id, v.payload),
    onSuccess: invalidate,
  })
  const undoMut = useMutation({
    mutationFn: (v: { id: string; payload: UndoEndPayload }) =>
      equipmentApi.undoEndEvent(tenantId as string, v.id, v.payload),
    onSuccess: invalidate,
  })
  const createMut = useMutation({
    mutationFn: (payload: CreateEventPayload) => equipmentApi.createEvent(tenantId as string, payload),
    onSuccess: invalidate,
  })
  const updateMut = useMutation({
    mutationFn: (v: { id: string; patch: UpdateEventPayload }) =>
      equipmentApi.updateEvent(tenantId as string, v.id, v.patch),
    onSuccess: invalidate,
  })

  const tasks = useMemo<MaintenanceTask[]>(() => {
    const base = useMockApi
      ? mockMaintenanceTasks.filter((t) => !query.overdue || t.overdue)
      : (eventsQuery.data ?? []).map(apiEventToTask)
    return [...localAdded, ...base].map((t) => (overrides[t.id] ? withStatusOverride(t, overrides[t.id]) : t))
  }, [eventsQuery.data, localAdded, overrides, query.overdue])

  const stats = useMemo<MyListStats>(() => statsOf(tasks), [tasks])

  // `onStarted` runs once the server has started it: a task with a form that
  // had to go through the Start dialog opens its form then (z8nrz7fgzw).
  // Awaited by the Start dialog, which shows a refusal where it was asked — a
  // storage unit that still holds stock asks for an override reason there.
  const start = useCallback(
    async (id: string, payload?: StartEventPayload, onStarted?: () => void) => {
      setOverrides((o) => ({ ...o, [id]: 'started' }))
      if (useMockApi || !payload) return
      try {
        await startMut.mutateAsync({ id, payload })
      } catch (err) {
        revertOverride(id)
        throw err
      }
      onStarted?.()
    },
    [startMut, revertOverride],
  )

  // Awaited by the End modal, which shows a refusal (e.g. the task's procedure
  // is not complete) where it was asked, so the row moves only once it is ended
  // and the error is the modal's, not the list's.
  const end = useCallback(
    async (id: string, payload?: EndEventPayload) => {
      if (!useMockApi && payload) {
        await endMut.mutateAsync({ id, payload })
        void qc.invalidateQueries({ queryKey: [PENDING_ACTIONS_KEY] })
      }
      setOverrides((o) => ({ ...o, [id]: 'completed' }))
    },
    [endMut, qc],
  )

  const undoEnd = useCallback(
    (id: string, payload?: UndoEndPayload) => {
      setOverrides((o) => ({ ...o, [id]: 'started' }))
      if (!useMockApi && payload) undoMut.mutate({ id, payload }, { onError: () => revertOverride(id) })
    },
    [undoMut, revertOverride],
  )

  // Add an event from the New Event modal (86ey7d8pq). In real mode it POSTs an
  // ad-hoc event and the row arrives when the events query refetches; in mock
  // mode it prepends a local `due` row from the display fallback.
  const createEvent = useCallback(
    (
      payload: CreateEventPayload,
      fallback: Omit<MaintenanceTask, 'id' | 'status'>,
      // Runs only once the row actually exists, and receives it. Callers use it
      // to say something about the task that was created; firing it on submit
      // instead would let them announce a task the server had rejected.
      onCreated?: (created: MaintenanceTask) => void,
    ) => {
      if (useMockApi) {
        const task = { ...fallback, id: `mt-new-${localAdded.length + 1}`, status: 'due' as const }
        setLocalAdded((prev) => [task, ...prev])
        onCreated?.(task)
        return
      }
      createMut.mutate(payload, { onSuccess: (created) => onCreated?.(apiEventToTask(created)) })
    },
    [createMut, localAdded.length],
  )

  // Reschedule / reassign (Team Schedule drag-and-drop + explicit assign).
  const updateEvent = useCallback(
    (id: string, patch: UpdateEventPayload) => {
      if (!useMockApi) updateMut.mutate({ id, patch })
    },
    [updateMut],
  )

  /**
   * Move a task's due date from its row (overdue tasks especially) and await
   * the server, so a refusal is shown beside the date that caused it rather
   * than in the page banner.
   */
  const rescheduleEvent = useCallback(
    async (id: string, dueDate: string, actor?: string) => {
      if (useMockApi) return
      await equipmentApi.updateEvent(tenantId as string, id, { dueDate, ...(actor ? { actor } : {}) })
      invalidate()
    },
    [tenantId, invalidate],
  )

  // Start's and End's refusals are shown by the modal that awaited them, not here.
  const mutationError = undoMut.error ?? createMut.error ?? updateMut.error

  return {
    tasks,
    // Raw events for consumers (e.g. the technician calendar) that need fields
    // the MaintenanceTask mapping drops (assigneeId, sopRef, plannedStart).
    events: eventsQuery.data ?? [],
    stats,
    start,
    end,
    undoEnd,
    createEvent,
    updateEvent,
    rescheduleEvent,
    isMutating:
      startMut.isPending || endMut.isPending || undoMut.isPending || createMut.isPending || updateMut.isPending,
    isLoading: eventsQuery.isLoading && eventsQuery.fetchStatus !== 'idle',
    error:
      eventsQuery.error instanceof Error
        ? eventsQuery.error.message
        : mutationError instanceof Error
          ? mutationError.message
          : null,
  }
}
